import React, { useContext, useEffect, useMemo, useState } from "react";
import { Card, Col, Grid, Row, Select, Skeleton, Space, Statistic, Tag, Typography, Avatar } from "antd";
import {
  BarChartOutlined,
  CalendarOutlined,
  HomeOutlined,
  MailOutlined,
  PhoneOutlined,
  PieChartOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import apiPath from "../constants/apiPath";
import useRequest from "../hooks/useRequest";
import { AuthContext } from "../context/AuthContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const { useBreakpoint } = Grid;
const { Option } = Select;
const { Title: TTitle, Text } = Typography;

const theme = {
  primary: "var(--theme1, #eca413)",
  primaryDark: "var(--theme1Dark, #132272)",
};

const adminStatsTemplate = {
  Hotels: 0,
  bus_company: 0,
  active_bookings: 0,
  total_passengers: 0,
  total_rooms: 0,
  used_rooms: 0,
  remaining_rooms: 0,
};

const hotelStatsTemplate = {
  agents: 0,
  hotels: 0,
  all_bookings: 0,
  all_confirmed_bookings: 0,
  total_rooms: 0,
  registered_rooms: 0,
  remaining_rooms: 0,
};

const adminMetricCards = [
  { key: "Hotels", title: "Hotels", icon: <HomeOutlined />, color: "#faad14", bg: "#fffbe6", path: "/hotel-management" },
  { key: "bus_company", title: "Bus Companies", icon: <UserOutlined />, color: "#fa1414ff", bg: "#fffbe6", path: "/bus-companies" },
  { key: "active_bookings", title: "Total Bookings", icon: <CalendarOutlined />, color: "#eb2f96", bg: "#fff0f6", path: "/bookings" },
  { key: "total_passengers", title: "Total Passengers registered", icon: <TeamOutlined />, color: "#52c41a", bg: "#f6ffed", path: "/admin/guest" },
  { key: "total_rooms", title: "Total Rooms", icon: <HomeOutlined />, color: "#1890ff", bg: "#e6f7ff", path: "/hotel-management" },
  { key: "used_rooms", title: "Rooms Used", icon: <PieChartOutlined />, color: "#1890ff", bg: "#e6f7ff", path: "/hotel-management" },
  { key: "remaining_rooms", title: "Rooms Remaining", icon: <PieChartOutlined />, color: "#722ed1", bg: "#f9f0ff", path: "/hotel-management" },
];

const hotelMetricCards = [
  { key: "total_rooms", title: "Total rooms", icon: <HomeOutlined />, color: "#faad14", bg: "#fffbe6", path: "/hotel-management" },
  { key: "registered_rooms", title: "Rooms registered/used", icon: <UserOutlined />, color: "#52c41a", bg: "#f6ffed", path: "/bookings" },
  { key: "remaining_rooms", title: "Rooms remaining", icon: <PieChartOutlined />, color: "#1890ff", bg: "#e6f7ff", path: "/hotel-management" },
  // { key: "all_bookings", title: "All Bookings", icon: <CalendarOutlined />, color: "#722ed1", bg: "#f9f0ff", path: "/bookings" },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { request } = useRequest();
  const { userProfile } = useContext(AuthContext);
  const authRole = localStorage.getItem("authRole") || "admin";
  const isHotelPanel = authRole === "hotel" || userProfile?.userType === "HotelPartner" || userProfile?.user_type === "HotelPartner";
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(isHotelPanel ? hotelStatsTemplate : adminStatsTemplate);
  const [superAdminDetails, setSuperAdminDetails] = useState(null);
  const [chartType, setChartType] = useState("bar");
  const [airlines, setAirlines] = useState([]);
  const [selectedAirline, setSelectedAirline] = useState("all");
  const screens = useBreakpoint();
  const metricCards = isHotelPanel ? hotelMetricCards : adminMetricCards;

  const getManagerId = () => {
    try {
      const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
      const loginData = JSON.parse(localStorage.getItem("loginData") || "{}");
      return userProfile?.Hotel_Id || profile?.Hotel_Id || loginData?.managerDetails?.Hotel_Id || null;
    } catch (_err) {
      return userProfile?.Hotel_Id || null;
    }
  };

  const values = useMemo(() => metricCards.map((card) => stats[card.key] || 0), [stats]);

  const barData = useMemo(
    () => ({
      labels: metricCards.map((c) => c.title),
      datasets: [
        {
          label: "Count",
          data: values,
          backgroundColor: metricCards.map((c) => c.color),
          borderRadius: screens.xs ? 8 : 10,
          borderSkipped: false,
          maxBarThickness: screens.xs ? 36 : 52,
        },
      ],
    }),
    [values, screens.xs]
  );

  const pieData = useMemo(
    () => ({
      labels: metricCards.map((c) => c.title),
      datasets: [
        {
          data: values,
          backgroundColor: metricCards.map((c) => c.color),
          borderColor: "#fff",
          borderWidth: 2,
          hoverOffset: 10,
        },
      ],
    }),
    [values]
  );

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { backgroundColor: "rgba(0,0,0,0.85)", cornerRadius: 10, padding: 10 },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: "rgba(0,0,0,0.05)" },
          ticks: { color: "#7d7d7d", font: { size: screens.xs ? 10 : 12 } },
        },
        x: {
          grid: { display: false },
          ticks: { color: "#7d7d7d", font: { size: screens.xs ? 9 : 11 }, maxRotation: 0 },
        },
      },
    }),
    [screens.xs]
  );

  const pieOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: screens.xs ? "bottom" : "right",
          labels: { usePointStyle: true, pointStyle: "circle", padding: screens.xs ? 10 : 16 },
        },
      },
    }),
    [screens.xs]
  );

  useEffect(() => {
    const managerId = getManagerId();
    setLoading(true);

    let url = isHotelPanel
      ? `${apiPath.baseURL}/hotel_partner_dashboard_count/${managerId}`
      : `${apiPath.baseURL}${apiPath.dashboardAllCount}`;

    if (!isHotelPanel && selectedAirline !== "all") {
      url += `?airline_id=${selectedAirline}`;
    }

    if (isHotelPanel && !managerId) {
      setLoading(false);
      return;
    }

    request({
      url,
      method: "GET",
      onSuccess: (res) => {
        if (res?.success) {
          if (res.airlines) setAirlines(res.airlines);

          setStats(
            isHotelPanel
              ? {
                agents: res.total_agents || res.agents || 0,
                hotels: res.total_hotels || res.hotels || 0,
                all_bookings: res.total_bookings || res.all_bookings || 0,
                all_confirmed_bookings:
                  res.confirmed_bookings || res.all_confirmed_bookings || 0,
                total_rooms: res.total_rooms || 0,
                registered_rooms: res.registered_rooms || 0,
                remaining_rooms: res.remaining_rooms || 0,
              }
              : {
                Hotels: res.Hotels || 0,
                bus_company: res.bus_company || 0,
                active_bookings: res.active_bookings || res.bookings || 0,
                total_passengers: res.total_passengers || 0,
                total_rooms: res.room_summary?.total || 0,
                used_rooms: res.room_summary?.used || 0,
                remaining_rooms: res.room_summary?.remaining || 0,
              },
          );
        } else {
          setStats(isHotelPanel ? hotelStatsTemplate : adminStatsTemplate);
        }
        setLoading(false);
      },
      onError: () => {
        setStats(isHotelPanel ? hotelStatsTemplate : adminStatsTemplate);
        setLoading(false);
      },
    });
  }, [request, isHotelPanel, userProfile, selectedAirline]);

  useEffect(() => {
    if (isHotelPanel) return;
    const storedProfile = localStorage.getItem("userProfile");
    if (!storedProfile) return;
    try {
      const parsed = JSON.parse(storedProfile);
      if (!parsed?._id) return;
      request({
        url: `${apiPath.baseURL}${apiPath.getSuperAdmin}/${parsed._id}`,
        method: "GET",
        onSuccess: (res) => {
          if (res?.success) setSuperAdminDetails(res.Details || res.data);
        },
      });
    } catch (_e) {
      // ignore invalid localStorage data
    }
  }, [request, isHotelPanel]);

  return (
    <div className="dashboard-page">
      <Card
        bordered={false}
        style={{ borderRadius: 14, marginBottom: 14, background: "transparent", border: "none", boxShadow: "0 8px 24px rgba(19,34,114,0.08)", position: "relative", overflow: "hidden" }}
        bodyStyle={{ padding: 0, background: "transparent", border: "none" }}
      >

        <TTitle level={screens.xs ? 4 : 3} style={{ margin: 0, fontWeight: 700 }}>Dashboard</TTitle>
        <Text type="secondary">
          {isHotelPanel
            ? "Monitor your agents, hotels and bookings at a glance."
            : "Monitor agents, hotels and bookings at a glance."}
        </Text>
      </Card>

      {!isHotelPanel && superAdminDetails && (
        <Card bordered={false} style={{ borderRadius: 14, marginBottom: 14, boxShadow: "0 8px 24px rgba(19,34,114,0.08)" }} bodyStyle={{ padding: screens.xs ? 14 : 18 }}>
          <Row gutter={[12, 10]} align="middle">
            <Col xs={24} md={5} style={{ textAlign: screens.xs ? "center" : "left" }}>
              <Avatar size={screens.xs ? 62 : 72} src={superAdminDetails.profileImage} icon={<UserOutlined />} style={{ border: `3px solid ${theme.primary}` }} />
            </Col>
            <Col xs={24} md={19}>
              <Row gutter={[12, 8]}>
                <Col xs={24} sm={12}>
                  <Text strong><UserOutlined style={{ marginRight: 8, color: theme.primary }} />{superAdminDetails.name || userProfile?.name || "Admin"}</Text>
                  <div style={{ marginTop: 6 }}><Tag color="blue">{superAdminDetails.userType || "Super Admin"}</Tag></div>
                </Col>
                <Col xs={24} sm={12}><Text type="secondary"><MailOutlined style={{ marginRight: 8 }} />{superAdminDetails.email || "-"}</Text></Col>
                {superAdminDetails.contact_no && (
                  <Col xs={24} sm={12}><Text type="secondary"><PhoneOutlined style={{ marginRight: 8 }} />{superAdminDetails.contact_no}</Text></Col>
                )}
                <Col xs={24} sm={12}><Text type="secondary">Status:</Text><Tag color={superAdminDetails.status === 1 ? "green" : "red"} style={{ marginLeft: 8 }}>{superAdminDetails.status === 1 ? "Active" : "Inactive"}</Tag></Col>
              </Row>
            </Col>
          </Row>
        </Card>
      )}

      <Row gutter={[12, 12]}>
        {metricCards.map((card) => (
          <Col xs={24} sm={12} lg={6} key={card.key}>
            <Card
              bordered={false}
              hoverable
              onClick={() => {
                if (card.key === "total_rooms" || card.key === "used_rooms" || card.key === "remaining_rooms") {
                  const airlineParam = selectedAirline !== "all" ? `&airline_id=${selectedAirline}` : "";
                  navigate(`${card.path}?view=${card.key}${airlineParam}`);
                } else {
                  navigate(card.path);
                }
              }}
              style={{ borderRadius: 14, boxShadow: "0 8px 24px rgba(19,34,114,0.08)", background: `#fff`, height: "100%" }}
              bodyStyle={{ padding: screens.xs ? 14 : 18 }}
            >
              <Skeleton active loading={loading} paragraph={{ rows: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                  <div style={{ width: 42, height: 42, borderRadius: "50%", backgroundColor: "#f9efdc", display: "flex", alignItems: "center", justifyContent: "center", color: "#eca413", fontSize: 18 }}>{card.icon}</div>
                  <Text type="secondary" style={{ fontWeight: 600 }}>{card.title}</Text>
                </div>
                <Statistic value={stats[card.key] ?? 0} valueStyle={{ color: "#eca413", fontSize: screens.xs ? 24 : 30, fontWeight: 800 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>{stats[card.key] === 1 ? "item" : "items"}</Text>
              </Skeleton>
            </Card>
          </Col>
        ))}
      </Row>

      <Card bordered={false} style={{ marginTop: 14, borderRadius: 14, boxShadow: "0 8px 24px rgba(19,34,114,0.08)" }} bodyStyle={{ padding: screens.xs ? 14 : 18 }}>
        <div style={{ display: "flex", flexDirection: screens.xs ? "column" : "row", justifyContent: "space-between", alignItems: screens.xs ? "stretch" : "center", gap: 10, marginBottom: 12 }}>
          <Space direction="vertical" size={0}>
            <TTitle level={screens.xs ? 5 : 4} style={{ margin: 0 }}>Overview</TTitle>
            <Text type="secondary" style={{ fontSize: 12 }}>Real-time statistics visualization</Text>
          </Space>
          <Space wrap>
            {!isHotelPanel && (
              <Select
                placeholder="Select Airline"
                value={selectedAirline}
                onChange={setSelectedAirline}
                style={{ width: screens.xs ? "100%" : 200 }}
              >
                <Option value="all">All Airlines</Option>
                {airlines.map(airline => (
                  <Option key={airline._id} value={airline._id}>{airline.name}</Option>
                ))}
              </Select>
            )}
            <Select value={chartType} onChange={setChartType} style={{ width: screens.xs ? "100%" : 160 }}>
              <Option value="bar"><BarChartOutlined style={{ marginRight: 6 }} />Bar Chart</Option>
              <Option value="pie"><PieChartOutlined style={{ marginRight: 6 }} />Pie Chart</Option>
            </Select>
          </Space>
        </div>
        <Skeleton active loading={loading} paragraph={{ rows: 6 }}>
          <div style={{ height: screens.xs ? 300 : 420, borderRadius: 12, padding: screens.xs ? 8 : 14, background: "#fff" }}>
            {chartType === "bar" ? <Bar data={barData} options={barOptions} /> : <Pie data={pieData} options={pieOptions} />}
          </div>
        </Skeleton>
      </Card>

      <style>{`
        .dashboard-page .ant-select-selector {
          border-radius: 10px !important;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
